import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/app/StatusBadge";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { ArrowLeft, MapPin, Clock, Users, Printer, Phone, Package, StickyNote, Send, CheckCircle2, FileDown, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import SignaturePad from "@/components/app/SignaturePad";
import PhotoGallery from "@/components/app/PhotoGallery";
import InvoicesPanel from "@/components/app/InvoicesPanel";
import FuelPanel from "@/components/app/FuelPanel";
import InspectionPanel from "@/components/app/InspectionPanel";
import WhatsAppDispatch from "@/components/app/WhatsAppDispatch";
import useAutoRefresh from "@/hooks/useAutoRefresh";
import ForemanLock from "@/components/app/ForemanLock";
import ExtraChargesPanel from "@/components/app/ExtraChargesPanel";
import Collapsible from "@/components/app/Collapsible";
import JobPhotoBoard from "@/components/app/JobPhotoBoard";
import IncidentAlerts from "@/components/app/IncidentAlerts";
import { useJobTranslation, TranslationBar } from "@/components/app/JobTranslation";

function FloorInfo({ floor, elevator, testid }) {
  if (!floor && !elevator) return null;
  return (
    <div className="mt-1 text-sm text-slate-600" data-testid={testid}>
      Piano <b className="text-slate-900">{floor || "?"}</b> · {elevator ? "con ascensore" : "senza ascensore"}
    </div>
  );
}

export default function JobDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const tr = useJobTranslation(job);
  const isForeman = typeof window !== "undefined" && !!sessionStorage.getItem("iot_foreman_token");

  const load = () => api.get(`/jobs/${id}`).then((r) => setJob(r.data));
  useEffect(() => { load(); }, [id]);
  useAutoRefresh(() => load(), 15000);

  const updateStatus = async (status) => {
    try {
      const { data } = await api.patch(`/jobs/${id}/status`, { status });
      setJob(data);
      toast.success("Stato aggiornato");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const [dispatching, setDispatching] = useState(false);
  const dispatchJob = async () => {
    if (!window.confirm(job.notified_at
      ? "Il lavoro è già stato inviato ai dipendenti. Reinviarlo?"
      : "Inviare questo lavoro ai dipendenti assegnati? Riceveranno email + notifica push.")) return;
    setDispatching(true);
    try {
      const { data } = await api.post(`/jobs/${id}/dispatch`);
      toast.success(`Inviato a ${data.sent_count} dipendent${data.sent_count === 1 ? "e" : "i"}`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setDispatching(false); }
  };

  if (!job) return <div className="text-slate-500 text-sm">Caricamento…</div>;

  const totalItems = job.items.reduce((s, i) => s + (i.quantity || 1), 0);
  const isCompleted = job.status === "completato";
  const isAdmin = user.role === "admin";
  // Lavoro completato: dipendente/caposquadra NON possono più modificare nulla. Admin sì.
  const canEdit = isAdmin || !isCompleted;

  return (
    <div className="space-y-8">
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900"
        data-testid="back-button"
      >
        <ArrowLeft className="w-4 h-4" /> Indietro
      </button>

      {isAdmin && <IncidentAlerts job={job} onChanged={setJob} />}
      <TranslationBar tr={tr} />

      <header className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-sky-600 font-semibold mb-2">Trasloco</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">{job.client_name}</h1>
          <div className="mt-3 flex items-center gap-3">
            <StatusBadge status={job.status} />
            <span className="font-mono text-xs text-slate-500">#{job.id.slice(0, 8).toUpperCase()}</span>
            {isAdmin && (
              <button
                onClick={async () => {
                  try {
                    const { data } = await api.post(`/jobs/${job.id}/collected`, { collected: !job.balance_collected });
                    setJob({ ...job, balance_collected: data.balance_collected, collected_at: data.collected_at });
                    toast.success(data.balance_collected ? "Saldo incassato: quote della squadra sbloccate" : "Saldo segnato come da incassare");
                  } catch (e) { toast.error(formatApiError(e)); }
                }}
                className={`text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full transition-colors ${job.balance_collected ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200" : "bg-amber-100 text-amber-800 hover:bg-amber-200"}`}
                data-testid="toggle-collected-button"
              >
                {job.balance_collected ? "Saldo incassato" : "Saldo da incassare"}
              </button>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {job.status === "completato" && user.role !== "admin" ? (
            <div className="inline-flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-xl font-semibold text-sm shadow-md shadow-emerald-600/30" data-testid="job-closed-chip">
              <CheckCircle2 className="w-4 h-4" /> Lavoro CHIUSO
            </div>
          ) : (
            <Select value={job.status} onValueChange={updateStatus}>
              <SelectTrigger className="w-44 bg-white" data-testid="change-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="programmato">Programmato</SelectItem>
                <SelectItem value="in_corso">In Corso</SelectItem>
                <SelectItem value="completato">Completato</SelectItem>
                {user.role === "admin" && <SelectItem value="annullato">Annullato</SelectItem>}
              </SelectContent>
            </Select>
          )}
          {user.role === "admin" && (job.assigned_employee_ids?.length || 0) > 0 && (
            <>
              <Button
                onClick={dispatchJob}
                disabled={dispatching}
                className={job.notified_at
                  ? "bg-slate-200 hover:bg-slate-300 text-slate-700 h-10"
                  : "bg-emerald-600 hover:bg-emerald-700 text-white h-10 animate-pulse"}
                data-testid="dispatch-job-button"
              >
                {job.notified_at ? <CheckCircle2 className="w-4 h-4 mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                {job.notified_at ? "Reinvia a dipendenti" : "Invia a dipendenti"}
              </Button>
              <WhatsAppDispatch jobId={job.id} />
            </>
          )}
          <Link to={`/stampa/${job.id}`} target="_blank">
            <Button variant="outline" className="h-10" data-testid="job-print-button">
              <Printer className="w-4 h-4 mr-2" /> Stampa
            </Button>
          </Link>
          {user.role === "admin" && job.status === "completato" && (
            <>
              <Button
                onClick={async () => {
                  try {
                    const r = await api.get(`/jobs/${job.id}/auto-invoice/download`, { responseType: "blob" });
                    const url = URL.createObjectURL(r.data);
                    window.open(url, "_blank");
                    setTimeout(() => URL.revokeObjectURL(url), 60000);
                  } catch (e) {
                    toast.error(formatApiError(e));
                  }
                }}
                className="h-10 bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="auto-invoice-download"
              >
                <FileDown className="w-4 h-4 mr-2" /> Scarica Ricevuta
              </Button>
              {job.client_phone && (
                <Button
                  onClick={async () => {
                    try {
                      const r = await api.get(`/jobs/${job.id}/auto-invoice/download`, { responseType: "blob" });
                      const name = `Ricevuta_${(job.client_name || "cliente").replace(/[^\w]+/g, "_")}_${job.scheduled_date || ""}.pdf`;
                      const file = new File([r.data], name, { type: "application/pdf" });
                      const text =
                        `Buongiorno ${job.client_name}, le inviamo la ricevuta del trasloco del ${job.scheduled_date}. ` +
                        `Totale: € ${(job.total_with_extras ?? job.total_amount ?? 0).toFixed(2)}. ` +
                        `Da saldare: € ${(job.settle_total ?? job.settle_amount ?? 0).toFixed(2)}. ` +
                        `Grazie! — In & Out Traslochi`;
                      if (navigator.canShare && navigator.canShare({ files: [file] })) {
                        try {
                          await navigator.share({ files: [file], text });
                        } catch (err) {
                          if (err?.name !== "AbortError") throw err;
                        }
                        return;
                      }
                      const url = URL.createObjectURL(file);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = name;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      setTimeout(() => URL.revokeObjectURL(url), 60000);
                      const phone = (job.client_phone || "").replace(/[^\d]/g, "");
                      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(text)}`, "_blank");
                      toast.success("PDF scaricato: allegalo nella chat WhatsApp del cliente");
                    } catch (e) {
                      toast.error(formatApiError(e));
                    }
                  }}
                  className="h-10 bg-emerald-500 hover:bg-emerald-600 text-white"
                  data-testid="auto-invoice-whatsapp"
                >
                  <MessageCircle className="w-4 h-4 mr-2" /> Invia Ricevuta WhatsApp
                </Button>
              )}
            </>
          )}
          {user.role !== "admin" && job.status === "completato" && isForeman && (
            <Button
              onClick={async () => {
                try {
                  const r = await api.get(`/jobs/${job.id}/auto-invoice/download`, { responseType: "blob" });
                  const url = URL.createObjectURL(r.data);
                  window.open(url, "_blank");
                  setTimeout(() => URL.revokeObjectURL(url), 60000);
                } catch (e) {
                  toast.error(formatApiError(e));
                }
              }}
              className="h-10 bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="auto-invoice-download-employee"
            >
              <FileDown className="w-4 h-4 mr-2" /> Scarica Ricevuta
            </Button>
          )}
        </div>
      </header>

      {user.role === "admin" && job.notified_at && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-2.5 flex items-center gap-2 text-sm text-emerald-800" data-testid="dispatch-notified-banner">
          <CheckCircle2 className="w-4 h-4" />
          <span>Lavoro inviato ai dipendenti il <b>{new Date(job.notified_at).toLocaleString("it-IT")}</b></span>
        </div>
      )}

      {user.role === "admin" && !job.notified_at && (job.assigned_employee_ids?.length || 0) > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 flex items-center gap-2 text-sm text-amber-800" data-testid="dispatch-pending-banner">
          <Send className="w-4 h-4" />
          <span>Il lavoro <b>non è ancora stato inviato ai dipendenti</b>. Completa tutti i dati e poi tocca "Invia a dipendenti".</span>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <section className="bg-white rounded-2xl border border-slate-200 p-6">
            <h3 className="font-display font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Clock className="w-4 h-4 text-sky-600" /> Programmazione
            </h3>
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <Field label="Data" value={job.scheduled_date} />
              <Field label="Orario cantiere" value={`${job.start_time}${job.end_time ? ` – ${job.end_time}` : ""}`} />
            </div>
            {job.depot_time && (
              <div className="mt-4 flex items-start gap-3 bg-amber-50 border-2 border-amber-300 rounded-xl p-4" data-testid="job-depot-time-banner">
                <div className="text-3xl">🏭</div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-amber-700">In deposito alle</div>
                  <div className="font-display text-2xl font-extrabold text-amber-900 font-mono">{job.depot_time}</div>
                  <div className="text-xs text-amber-700 mt-0.5">Tutti i dipendenti presenti in sede prima di partire</div>
                </div>
              </div>
            )}
            {job.foreman && (
              <div className="mt-3 inline-flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-2.5" data-testid="job-foreman-badge">
                <span className="text-xl">👷</span>
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-indigo-700">Caposquadra</div>
                  <div className="font-bold text-indigo-900">{job.foreman.name}</div>
                </div>
              </div>
            )}
          </section>

          {job.materials && (
            <section className="bg-gradient-to-br from-amber-50 to-amber-100 border-2 border-amber-300 rounded-2xl p-6 shadow-sm" data-testid="job-materials-section">
              <h3 className="font-display font-bold text-amber-900 mb-2 flex items-center gap-2 text-lg">
                🧰 Note materiali da portare
              </h3>
              <p className="text-[11px] text-amber-800/80 mb-3 uppercase tracking-widest font-bold">Cosa i dipendenti devono avere con sé</p>
              <div className="bg-white rounded-xl border border-amber-200 p-4 text-sm text-amber-900 whitespace-pre-line font-medium leading-relaxed" data-no-translate data-testid="job-materials-text">
                {tr.t("materials", job.materials)}
              </div>
            </section>
          )}

          {/* Foto Materiali da utilizzare (admin only) */}
          {(isAdmin || (job.materials_photos || []).length > 0) && (
            <Collapsible
              title="Foto materiali da utilizzare"
              hint={`${(job.materials_photos || []).filter((p) => !p.is_deleted).length} foto`}
              defaultOpen={(job.materials_photos || []).some((p) => !p.is_deleted)}
              testid="materials-photos-collapsible"
            >
            <JobPhotoBoard
              job={job}
              kind="materials"
              title="Foto materiali da utilizzare"
              subtitle="Foto dei materiali che portate per questo trasloco (scatoloni, pluriball, cinghie…)"
              canUpload={isAdmin}
              onChanged={(updated) => setJob(updated)}
              colorScheme="indigo"
            />
            </Collapsible>
          )}

          {/* Durata effettiva vs media del tipo */}
          {job.time_anomaly && (
            <section className="bg-sky-50 border-2 border-sky-300 rounded-2xl p-5 flex items-start gap-3" data-testid="time-anomaly-banner">
              <Clock className="w-6 h-6 text-sky-700 shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-display font-bold text-sky-900">
                  ⏱️ Lavoro più lungo del solito — +{job.time_anomaly.deviation_pct.toFixed(1)}% sopra la media
                </div>
                <div className="text-xs text-sky-800 font-mono mt-1 space-x-3">
                  <span>Tipo: <b>{job.time_anomaly.job_type}</b></span>
                  <span>Durata effettiva: <b>{job.time_anomaly.actual_hours.toFixed(1)}h</b></span>
                  <span>Media storica: <b>{job.time_anomaly.median_hours.toFixed(1)}h</b> ({job.time_anomaly.samples} lavori)</span>
                </div>
              </div>
            </section>
          )}
          {job.actual_hours && !job.time_anomaly && job.job_type && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-2.5 text-sm text-emerald-800 flex items-center gap-2" data-testid="time-in-range-banner">
              <Clock className="w-4 h-4" />
              <span>Durata effettiva <b>{job.actual_hours.toFixed(1)}h</b> — nella norma per i <b>{job.job_type}</b>.</span>
            </div>
          )}

          <section className="bg-white rounded-2xl border border-slate-200 p-6">
            <h3 className="font-display font-bold text-slate-900 mb-4 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-sky-600" /> Indirizzi
            </h3>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="text-xs uppercase tracking-wider font-semibold text-slate-400">Partenza</div>
                  <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(job.address_from)}`}
                     target="_blank" rel="noreferrer"
                     className="text-xs text-sky-600 hover:text-sky-700 font-semibold inline-flex items-center gap-1"
                     data-testid="maps-from-button">
                    Apri in Maps →
                  </a>
                </div>
                <div className="text-slate-900 font-medium">{job.address_from}</div>
                <FloorInfo floor={job.floor_from} elevator={job.elevator_from} testid="job-floor-from" />
              </div>
              <div className="border-t border-dashed border-slate-200" />
              <div>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="text-xs uppercase tracking-wider font-semibold text-slate-400">Arrivo</div>
                  <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(job.address_to)}`}
                     target="_blank" rel="noreferrer"
                     className="text-xs text-emerald-600 hover:text-emerald-700 font-semibold inline-flex items-center gap-1"
                     data-testid="maps-to-button">
                    Apri in Maps →
                  </a>
                </div>
                <div className="text-slate-900 font-medium">{job.address_to}</div>
                <FloorInfo floor={job.floor_to} elevator={job.elevator_to} testid="job-floor-to" />
              </div>
            </div>
          </section>

          <section className="bg-white rounded-2xl border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-slate-900 flex items-center gap-2">
                <Package className="w-4 h-4 text-sky-600" /> Oggetti da Traslocare
              </h3>
              <span className="text-xs font-mono text-slate-500">{totalItems} pezzi</span>
            </div>
            {job.items.length === 0 ? (
              <div className="text-sm text-slate-400 italic">Nessun oggetto registrato.</div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {job.items.map((it, idx) => (
                  <li key={idx} className="py-3 flex items-start gap-3">
                    <span className="font-mono text-xs text-sky-600 font-bold bg-sky-50 border border-sky-200 rounded px-2 py-0.5 min-w-12 text-center">×{it.quantity}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center flex-wrap gap-2">
                        <span className="font-medium text-slate-900" data-no-translate data-testid={`job-item-name-${idx}`}>{tr.item(idx, "name", it.name)}</span>
                        {it.category && it.category !== "altro" && (
                          <span className="text-[10px] uppercase font-bold tracking-wider bg-slate-100 text-slate-500 border border-slate-200 rounded-full px-2 py-0.5">
                            {it.category}
                          </span>
                        )}
                        {it.smontaggio && (
                          <span className="text-[10px] uppercase font-bold tracking-wider bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-full px-2 py-0.5" title="Da smontare">
                            🔧 Smontaggio
                          </span>
                        )}
                        {it.rimontaggio && (
                          <span className="text-[10px] uppercase font-bold tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5" title="Da rimontare">
                            🔨 Rimontaggio
                          </span>
                        )}
                      </div>
                      {it.note && <div className="text-xs text-slate-500 mt-0.5" data-no-translate>{tr.item(idx, "note", it.note)}</div>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <InspectionPanel
            job={job}
            canEdit={canEdit && (isAdmin || job.assigned_employee_ids?.includes(user.id))}
            onChanged={(updated) => setJob(updated)}
          />

          {/* Foto Attrezzatura (illimitate, dipendenti + admin) */}
          <JobPhotoBoard
            job={job}
            kind="equipment"
            title="Foto attrezzatura usata"
            subtitle="Foto dell'attrezzatura effettivamente utilizzata in questo trasloco (nessun limite di numero)."
            canUpload={canEdit && (isAdmin || job.assigned_employee_ids?.includes(user.id))}
            onChanged={(updated) => setJob(updated)}
            colorScheme="violet"
          />

          <ForemanLock onUnlocked={load}>
            {(job.total_amount != null) && (
              <section className="bg-gradient-to-br from-emerald-50 to-sky-50 border-2 border-emerald-200 rounded-2xl p-6" data-testid="job-amounts-summary">
                <h3 className="font-display font-bold text-emerald-900 mb-3 flex items-center gap-2">💰 Importi concordati</h3>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Totale</div>
                    <div className="font-display text-xl font-extrabold text-slate-900 mt-1" data-testid="total-with-extras">
                      {new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(job.total_with_extras ?? job.total_amount ?? 0))}
                    </div>
                    {Number(job.extra_charges_total || 0) > 0 && (
                      <div className="text-[10px] text-emerald-700 mt-0.5 font-mono">
                        incl. +{new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(job.extra_charges_total || 0))}
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Acconto</div>
                    <div className="font-display text-xl font-extrabold text-slate-700 mt-1">
                      {new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Math.max(0, Number(job.total_amount || 0) - Number(job.settle_amount || 0)))}
                    </div>
                  </div>
                  <div className="bg-emerald-600 text-white rounded-xl p-2">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-100">Da Saldare</div>
                    <div className="font-display text-xl font-extrabold mt-1" data-testid="settle-total">
                      {new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(job.settle_total ?? job.settle_amount ?? 0))}
                    </div>
                    {Number(job.extra_charges_total || 0) > 0 && (
                      <div className="text-[10px] text-emerald-100 mt-1 font-mono">
                        incl. +{new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(job.extra_charges_total || 0))}
                      </div>
                    )}
                  </div>
                </div>
                {isAdmin && Number(job.total_amount || 0) > Number(job.settle_amount || 0) && (
                  <div className="mt-3 text-xs text-slate-600 flex flex-wrap gap-3" data-testid="job-deposit-info">
                    <span>Acconto versato: <b>{job.deposit_paid_at ? job.deposit_paid_at.split("-").reverse().join("/") : "data non indicata"}</b></span>
                    <span className={job.deposit_receipt_received ? "text-emerald-700 font-semibold" : "text-amber-700 font-semibold"}>
                      {job.deposit_receipt_received ? "Contabile ricevuta" : "Contabile da ricevere"}
                    </span>
                  </div>
                )}
              </section>
            )}

            <PhotoGallery
              job={job}
              canEdit={canEdit && (isAdmin || job.assigned_employee_ids?.includes(user.id))}
              onChanged={(updated) => setJob(updated)}
            />

            <Collapsible
              title="Aggiunte ed extra del cliente"
              hint={(job.extra_charges || []).length ? `${job.extra_charges.length} aggiunte` : "nessuna"}
              defaultOpen={(job.extra_charges || []).length > 0}
              testid="extra-charges-collapsible"
            >
              <ExtraChargesPanel job={job} onChanged={(updated) => setJob(updated)} />
            </Collapsible>

            <FuelPanel
              job={job}
              canEdit={canEdit && (isAdmin || job.assigned_employee_ids?.includes(user.id))}
              onChanged={(updated) => setJob(updated)}
            />

            <InvoicesPanel job={job} onChanged={(updated) => setJob(updated)} />
          </ForemanLock>

          <SignaturePad
            job={job}
            kind="client"
            canEdit={canEdit && (isAdmin || job.assigned_employee_ids?.includes(user.id))}
            canClear={isAdmin}
            onSaved={(updated) => setJob(updated)}
          />

          <SignaturePad
            job={job}
            kind="employee"
            canEdit={canEdit && (isAdmin || job.assigned_employee_ids?.includes(user.id))}
            canClear={isAdmin || job.employee_signer_id === user.id}
            onSaved={(updated) => setJob(updated)}
          />

          {job.notes && (
            <section className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-display font-bold text-amber-900 mb-3 flex items-center gap-2">
                <StickyNote className="w-4 h-4" /> Note
              </h3>
              <div className="text-sm text-amber-900 whitespace-pre-line" data-no-translate data-testid="job-notes-text">{tr.t("notes", job.notes)}</div>
            </section>
          )}
        </div>

        <aside className="space-y-6">
          <section className="bg-white rounded-2xl border border-slate-200 p-6">
            <h3 className="font-display font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Phone className="w-4 h-4 text-sky-600" /> Cliente
            </h3>
            <div className="space-y-2 text-sm">
              <div className="font-semibold text-slate-900">{job.client_name}</div>
              {job.client_phone && (
                <a href={`tel:${job.client_phone}`} className="text-sky-600 hover:text-sky-700 font-mono text-xs">
                  {job.client_phone}
                </a>
              )}
            </div>
          </section>

          <section className="bg-white rounded-2xl border border-slate-200 p-6">
            <h3 className="font-display font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Users className="w-4 h-4 text-sky-600" /> Squadra
            </h3>
            {(job.assigned_employees || []).length === 0 ? (
              <div className="text-sm text-slate-400 italic">Nessun dipendente assegnato.</div>
            ) : (
              <ul className="space-y-2">
                {job.assigned_employees.map((e) => (
                  <li key={e.id} className="flex items-center gap-3 py-2">
                    <div className="w-9 h-9 rounded-full bg-sky-100 text-sky-700 flex items-center justify-center font-bold text-sm">
                      {e.name.split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-sm text-slate-900 truncate">{e.name}</div>
                      {e.phone && <div className="text-xs text-slate-500 font-mono">{e.phone}</div>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </aside>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider font-semibold text-slate-400 mb-1">{label}</div>
      <div className="text-slate-900 font-medium">{value}</div>
    </div>
  );
}
